import React, { useState, useMemo, useEffect } from 'react';
import { ReviewData } from '../Helpers/ReviewDataHelpers';
import { LocationReviewStats, transformLocationReviewData, prepareReviewsForSummary } from '../Helpers/ReviewDataHelpers';
import { exportAllLocationsToCSV, exportLocationReviewsToCSV } from '../Helpers/CSVExportHelpers';
import { Button, ButtonColor, Spinner } from '@swivl-mono/ui';
import { AiFillStar } from 'react-icons/ai';
import { AIModel } from '../../../Models/AI/AI.Model';
import { Member } from '../../../Models/Member/Member.Current';
import { useMergedLocationVariables } from '../../../Hooks/useLocationVariables';
import { LocationVarKey } from '@swivl-mono/types';
import '../../../Views/Reviews/Reviews.View.scss';

interface ViewAllReviewLocationsProps {
  reviewData: ReviewData | null;
  locationNames: { [key: number]: string };
  onViewLess: () => void;
  startDay: string;
  endDay: string;
  loadReviewData?: () => Promise<void>;
}

export const ViewAllReviewLocations: React.FC<ViewAllReviewLocationsProps> = ({
  reviewData,
  locationNames,
  onViewLess,
  startDay,
  endDay,
  loadReviewData
}) => {
  const [expandedLocations, setExpandedLocations] = useState<Set<number>>(new Set());
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState<boolean>(false);
  const [currentLocationSummary, setCurrentLocationSummary] = useState<LocationReviewStats | null>(null);
  const [cachedReviewData, setCachedReviewData] = useState<ReviewData | null>(null);
  const [cachedLocationNames, setCachedLocationNames] = useState<{ [key: number]: string }>({});
  const { generatedReviewSummaries } = AIModel.useState();

  // Use cached data if current data is null (during date change)
  const activeReviewData = reviewData || cachedReviewData;
  const activeLocationNames = Object.keys(locationNames).length > 0 ? locationNames : cachedLocationNames;

  const locationStats = useMemo(() => {
    if (!activeReviewData) return [];
    return transformLocationReviewData(activeReviewData, activeLocationNames);
  }, [activeReviewData, activeLocationNames]);

  const summaryStats = useMemo(() => {
    const totalLocations = locationStats.length;
    const totalReviews = locationStats.reduce((sum, loc) => sum + loc.totalReviews, 0);
    const avgRating = totalReviews > 0
      ? locationStats.reduce((sum, loc) => sum + (loc.averageRating * loc.totalReviews), 0) / totalReviews
      : 0;

    return { totalLocations, totalReviews, avgRating };
  }, [locationStats]);

  const currentSummary = useMemo(() => {
    const orgId = Member.orgId!;
    let cacheKey: string;

    if (currentLocationSummary) {
      const startDateKey = `${startDay}-loc${currentLocationSummary.locationId}` || 'no-start';
      const endDateKey = `${endDay}-loc${currentLocationSummary.locationId}` || 'no-end';
      cacheKey = `${orgId}-${startDateKey}-${endDateKey}-${currentLocationSummary.allReviews.length}`;
    } else {
      cacheKey = `${orgId}-${startDay || 'no-start'}-${endDay || 'no-end'}-${summaryStats.totalReviews}`;
    }

    return generatedReviewSummaries[cacheKey];
  }, [startDay, endDay, summaryStats.totalReviews, generatedReviewSummaries, currentLocationSummary]);

  // Reset showSummary if there's no valid summary in cache
  useEffect(() => {
    if (showSummary && (!currentSummary || currentSummary === "loading" || currentSummary.trim().length === 0)) {
      setShowSummary(false);
    }
  }, [showSummary, currentSummary]);

  // Cache the review data when it's available
  useEffect(() => {
    if (reviewData && Object.keys(locationNames).length > 0) {
      setCachedReviewData(reviewData);
      setCachedLocationNames(locationNames);
    }
  }, [reviewData, locationNames]);

  // Load review data when needed
  useEffect(() => {
    if (loadReviewData && !reviewData) {
      loadReviewData();
    }
  }, [loadReviewData, reviewData]);

  const toggleLocationExpanded = (locationId: number) => {
    const newExpanded = new Set(expandedLocations);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedLocations(newExpanded);
  };

  const handleGenerateSummary = async () => {
    const orgId = Member.orgId!;
    const allReviews = prepareReviewsForSummary(locationStats);

    setIsGeneratingSummary(true);
    setCurrentLocationSummary(null);

    try {
      const result = await AIModel.generateReviewSummary(orgId, allReviews, activeLocationNames, startDay, endDay);
      if (result && result.trim().length > 0) {
        setShowSummary(true);
      }
    } catch (error) {
      setShowSummary(false);
      alert('Failed to generate summary. Please try again with a smaller date range.');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleExportAll = () => {
    exportAllLocationsToCSV(locationStats, activeLocationNames);
  };

  const handleExportLocation = (stats: LocationReviewStats) => {
    const locationName = activeLocationNames[stats.locationId] || `Location ${stats.locationId}`;
    exportLocationReviewsToCSV(locationName, stats.allReviews);
  };

  const handleGenerateLocationSummary = async (stats: LocationReviewStats) => {
    const orgId = Member.orgId!;
    const locationName = activeLocationNames[stats.locationId] || `Location ${stats.locationId}`;

    setIsGeneratingSummary(true);
    setCurrentLocationSummary(stats);

    try {
      const result = await AIModel.generateReviewSummary(
        orgId,
        stats.allReviews,
        { [stats.locationId]: locationName },
        `${startDay}-loc${stats.locationId}`,
        `${endDay}-loc${stats.locationId}`
      );
      if (result && result.trim().length > 0) {
        setShowSummary(true);
      }
    } catch (error) {
      setShowSummary(false);
      alert('Failed to generate summary. Please try again.');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const StarRating: React.FC<{ rating: number; fontSize?: string }> = ({ rating, fontSize = '16px' }) => {
    return (
      <div style={{
        display: 'flex',
        fontSize,
        color: '#DAD3D0'
      }}>
        {[1, 2, 3, 4, 5].map(star => (
          <AiFillStar
            key={star}
            style={{
              color: rating >= star ? '#FFC107' : '#DAD3D0'
            }}
          />
        ))}
      </div>
    );
  };

  const renderRatingDistribution = (stats: LocationReviewStats) => {
    const maxCount = Math.max(...Object.values(stats.ratingDistribution));

    return (
      <div style={{ marginBottom: '20px', marginTop: '15px' }}>
        <h4 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>
          Rating Distribution
        </h4>
        {[5, 4, 3, 2, 1].map(rating => {
          const count = stats.ratingDistribution[rating] || 0;
          const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
          const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;

          return (
            <div key={rating} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
              fontSize: '13px'
            }}>
              <span style={{ width: '100px', color: '#666', display: 'flex', alignItems: 'center', gap: '2px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <AiFillStar
                    key={star}
                    style={{
                      color: star <= rating ? '#FFC107' : '#DAD3D0',
                      fontSize: '12px'
                    }}
                  />
                ))}
              </span>
              <div style={{
                width: '30%',
                height: '16px',
                backgroundColor: '#f0f0f0',
                borderRadius: '3px',
                margin: '0 10px',
                position: 'relative'
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: '#666',
                  borderRadius: '3px',
                  width: `${barWidth}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <span style={{ width: '80px', textAlign: 'right', color: '#666' }}>
                {count} ({percentage.toFixed(0)}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderReviews = (stats: LocationReviewStats) => {
    if (stats.allReviews.length === 0) {
      return (
        <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
          No reviews for this location.
        </div>
      );
    }

    return (
      <div>
        <h4 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>
          All Reviews
        </h4>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {stats.allReviews.map((review, index) => (
            <div key={index} style={{
              padding: '12px',
              marginBottom: '8px',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                fontSize: '13px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <StarRating rating={review.rating} fontSize="13px" />
                  <span style={{ fontWeight: 600, color: '#333' }}>
                    {review.reviewerName || 'Anonymous'}
                  </span>
                </div>
                <span style={{ color: '#333', fontWeight: 400 }}>
                  {new Date(review.reviewDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              {review.text && (
                <div style={{
                  fontSize: '13px',
                  lineHeight: '1.4',
                  color: '#333',
                  fontWeight: 400
                }}>
                  {review.text}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Check if reviewData needs to be loaded for the current date range
  const isDataLoading = !activeReviewData && !cachedReviewData;
  const hasNoData = activeReviewData && locationStats.length === 0;

  // Show loading state only when data is actually loading
  if (isDataLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: '80px',
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <Spinner />
        <div style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
          Loading review data...
        </div>
      </div>
    );
  }

  return (
    <div className="ReviewsView" style={{
      position: 'fixed',
      top: '80px',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#fff',
      zIndex: 9999
    }}>
      <div className="mainCol" style={{
        overflowY: 'auto',
        right: (showSummary && currentSummary && currentSummary !== "loading" && currentSummary.trim().length > 0) ? '300px' : '0'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
              onClick={onViewLess}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                cursor: 'pointer',
                padding: '8px 0'
              }}
            >
              ← Back
            </button>
            <h1 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 400,
              color: '#333',
              fontFamily: '"Source Sans Pro", sans-serif'
            }}>
              Google Review Location Analytics
            </h1>
          </div>
          {!hasNoData && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                color={ButtonColor.BLACK}
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary}
              >
                {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
              </Button>
              <Button
                color={ButtonColor.WHITE}
                onClick={handleExportAll}
                disabled={isGeneratingSummary}
              >
                Export CSV
              </Button>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          gap: '40px',
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Locations</div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#333' }}>{summaryStats.totalLocations.toLocaleString()}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Reviews</div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#333' }}>{summaryStats.totalReviews.toLocaleString()}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Average Rating</div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              {summaryStats.avgRating.toFixed(1)} <AiFillStar style={{ color: '#FFC107', fontSize: '24px' }} />
            </div>
          </div>
        </div>

        <div>
          {hasNoData ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#666',
              fontSize: '16px'
            }}>
              <div style={{ marginBottom: '10px', fontSize: '48px' }}>📊</div>
              <div>No review data available for the selected date range.</div>
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#999' }}>
                Try adjusting the date range to see review data.
              </div>
            </div>
          ) : (
            locationStats.map(stats => {
              const isExpanded = expandedLocations.has(stats.locationId);
              const locationName = activeLocationNames[stats.locationId] || `Location ${stats.locationId}`;

              const addressVars = useMergedLocationVariables(
                [LocationVarKey.Address, LocationVarKey.City, LocationVarKey.State, LocationVarKey.PostalCode],
                undefined,
                stats.locationId
              );

              const addressParts = [
                addressVars?.[LocationVarKey.Address]?.value,
                addressVars?.[LocationVarKey.City]?.value,
                addressVars?.[LocationVarKey.State]?.value,
                addressVars?.[LocationVarKey.PostalCode]?.value
              ].filter(Boolean);

              const fullAddress = addressParts.join(', ');

              return (
                <div key={stats.locationId} style={{
                  marginBottom: '20px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  <div
                    onClick={() => toggleLocationExpanded(stats.locationId)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: isExpanded ? '1px solid #f0f0f0' : 'none'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                        {locationName}
                      </div>
                      {fullAddress && (
                        <div style={{ fontSize: '14px', color: '#999', marginBottom: '4px' }}>
                          {fullAddress}
                        </div>
                      )}
                      <div style={{ fontSize: '16px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {stats.totalReviews.toLocaleString()} reviews • {stats.averageRating.toFixed(1)} <AiFillStar style={{ color: '#FFC107', fontSize: '14px' }} /> average
                      </div>
                    </div>
                    <div style={{ fontSize: '16px', color: '#666' }}>
                      {isExpanded ? '▼' : '▶'}
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 20px 20px' }}>
                      {renderRatingDistribution(stats)}
                      {renderReviews(stats)}
                      <div style={{ marginTop: '15px', textAlign: 'right', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <Button
                          color={ButtonColor.BLACK}
                          onClick={() => handleGenerateLocationSummary(stats)}
                          disabled={isGeneratingSummary}
                        >
                          Generate Summary
                        </Button>
                        <Button
                          color={ButtonColor.WHITE}
                          onClick={() => handleExportLocation(stats)}
                          disabled={isGeneratingSummary}
                        >
                          Export CSV
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {showSummary && currentSummary && currentSummary !== "loading" && currentSummary.trim().length > 0 && (
        <div className="sideCol">
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                {currentLocationSummary
                  ? `AI Summary - ${activeLocationNames[currentLocationSummary.locationId] || `Location ${currentLocationSummary.locationId}`}`
                  : 'AI Summary - All Locations'
                }
              </h3>
              <button
                onClick={() => {
                  setShowSummary(false);
                  setCurrentLocationSummary(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              color: '#333',
              fontWeight: 400
            }}>
              {currentSummary}
            </div>
            <div style={{ marginTop: '20px' }}>
              <Button
                color={ButtonColor.WHITE}
                onClick={() => navigator.clipboard.writeText(currentSummary)}
              >
                Copy to Clipboard
              </Button>
            </div>
          </div>
        </div>
      )}

      {isGeneratingSummary && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Spinner />
          <div style={{
            marginTop: '20px',
            fontSize: '18px',
            fontWeight: 600,
            color: '#333',
            textAlign: 'center'
          }}>
            🤖 Generating Summary...
          </div>
          <div style={{
            marginTop: '8px',
            fontSize: '14px',
            color: '#666',
            textAlign: 'center'
          }}>
            {currentLocationSummary
              ? `Analyzing ${currentLocationSummary.totalReviews} reviews for ${locationNames[currentLocationSummary.locationId] || `Location ${currentLocationSummary.locationId}`}`
              : `Analyzing ${summaryStats.totalReviews} reviews across ${summaryStats.totalLocations} locations`
            }
          </div>
          <div style={{
            marginTop: '8px',
            fontSize: '13px',
            color: '#999',
            textAlign: 'center'
          }}>
            This may take a few seconds...
          </div>
        </div>
      )}
    </div>
  );
};